/**
 * SessionStorage Utility
 *
 * Type-safe helper functions for managing session tokens in sessionStorage.
 * SessionStorage is automatically cleared when the browser tab is closed,
 * providing enhanced security for authentication tokens.
 *
 * Usage:
 *   setSessionToken('abc123')
 *   const token = getSessionToken()
 *   clearSessionToken()
 */

const SESSION_TOKEN_KEY = 'session_token';
const USER_DATA_KEY = 'user_data';

/**
 * Store authentication token in sessionStorage
 * @param token - JWT or session token from backend
 */
export const setSessionToken = (token: string): void => {
    try {
        sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    } catch (error) {
        console.error('Failed to save session token:', error);
    }
};

/**
 * Retrieve authentication token from sessionStorage
 * Checks both individual user token and corporate user token
 * @returns Session token or null if not found
 */
export const getSessionToken = (): string | null => {
    try {
        // Check for individual user token first
        const individualToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
        if (individualToken) {
            return individualToken;
        }

        // Check for corporate user token
        const corporateToken = sessionStorage.getItem('corporate_auth_token');
        if (corporateToken) {
            return corporateToken;
        }

        return null;
    } catch (error) {
        console.error('Failed to retrieve session token:', error);
        return null;
    }
};

/**
 * Remove authentication token from sessionStorage
 */
export const clearSessionToken = (): void => {
    try {
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
    } catch (error) {
        console.error('Failed to clear session token:', error);
    }
};

/**
 * Store user data in sessionStorage
 * @param userData - User object to store
 */
export const setUserData = (userData: any): void => {
    try {
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
    } catch (error) {
        console.error('Failed to save user data:', error);
    }
};

/**
 * Retrieve user data from sessionStorage
 * @returns Parsed user object or null if not found
 */
export const getUserData = (): any | null => {
    try {
        const data = sessionStorage.getItem(USER_DATA_KEY);
        return data ? JSON.parse(data) : null;
    } catch (error) {
        console.error('Failed to retrieve user data:', error);
        return null;
    }
};

/**
 * Remove user data from sessionStorage
 */
export const clearUserData = (): void => {
    try {
        sessionStorage.removeItem(USER_DATA_KEY);
    } catch (error) {
        console.error('Failed to clear user data:', error);
    }
};

/**
 * Clear individual user authentication data from sessionStorage.
 * Removes individual token keys plus the shared session_token and user_data keys.
 */
export const clearIndividualAuthData = (): void => {
    try {
        sessionStorage.removeItem('individual_auth_token');
        sessionStorage.removeItem('individual_token_type');
        sessionStorage.removeItem('individual_token_expires_at');
        sessionStorage.removeItem('individual_user');
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
        sessionStorage.removeItem(USER_DATA_KEY);
    } catch (error) {
        console.error('Failed to clear individual auth data:', error);
    }
};

/**
 * Clear corporate user authentication data from sessionStorage.
 * Removes corporate-prefixed token keys only.
 */
export const clearCorporateAuthData = (): void => {
    try {
        sessionStorage.removeItem('corporate_auth_token');
        sessionStorage.removeItem('corporate_token_type');
        sessionStorage.removeItem('corporate_token_expires_at');
        sessionStorage.removeItem('corporate_user');
    } catch (error) {
        console.error('Failed to clear corporate auth data:', error);
    }
};

/**
 * Clear admin authentication data from sessionStorage.
 * Removes admin-prefixed token keys only.
 */
export const clearAdminAuthData = (): void => {
    try {
        sessionStorage.removeItem('admin_auth_token');
        sessionStorage.removeItem('admin_token_type');
        sessionStorage.removeItem('admin_token_expires_at');
        sessionStorage.removeItem('admin_user');
    } catch (error) {
        console.error('Failed to clear admin auth data:', error);
    }
};

/**
 * Validate the individual authentication token stored in sessionStorage.
 * Checks presence and expiration of the individual_auth_token.
 *
 * @returns Object with isValid flag, the token (or null), an optional reason,
 *          and remainingMs when the token is valid and has a known expiry.
 */
export const validateIndividualToken = (): {
    isValid: boolean;
    token: string | null;
    reason?: string;
    remainingMs?: number;
} => {
    try {
        const token = sessionStorage.getItem('individual_auth_token');

        if (!token) {
            return { isValid: false, token: null, reason: 'missing' };
        }

        const expiresAt = sessionStorage.getItem('individual_token_expires_at');

        if (!expiresAt) {
            return { isValid: true, token, reason: 'no_expiry' };
        }

        const expirationDate = new Date(expiresAt);

        if (isNaN(expirationDate.getTime())) {
            return { isValid: true, token, reason: 'no_expiry' };
        }

        const remainingMs = expirationDate.getTime() - Date.now();

        if (remainingMs <= 0) {
            return { isValid: false, token, reason: 'expired' };
        }

        return { isValid: true, token, remainingMs };
    } catch (error) {
        console.error('Failed to validate individual token:', error);
        return { isValid: false, token: null, reason: 'missing' };
    }
};

/**
 * Clear all authentication-related data from sessionStorage.
 * Clears individual, corporate, and admin user authentication.
 */
export const clearAllAuthData = (): void => {
    clearIndividualAuthData();
    clearCorporateAuthData();
    clearAdminAuthData();
};

/**
 * Check if user is authenticated (has valid session token)
 * @returns true if session token exists
 */
export const hasSessionToken = (): boolean => {
    return getSessionToken() !== null;
};
